/**
 * Plain-language labels for the scan kiosk at the front desk (#2691). Pure, so
 * the rail, the Devices page and their tests share one wording.
 */
import { playerHandleSuffix } from "@bw/shared/components";
import type {
  KioskCheckinItem,
  KioskDevice,
  KioskMode,
  KioskPurpose,
  KioskRedReason,
  KioskUndoResult,
} from "@bw/shared/types/generated";

export const KIOSK_REASON_LABEL: Record<KioskRedReason, string> = {
  already_used: "Already used",
  expired_code: "Code expired",
  not_valid_here: "Not valid here",
  not_valid_now: "Not valid now",
  waiver_needed: "Waiver needed",
};

export const KIOSK_MODE_LABEL: Record<KioskMode, string> = {
  automatic: "Automatic",
  staff_confirms: "Staff confirms",
  off: "Off",
};

export const KIOSK_PURPOSE_LABEL: Record<KioskPurpose, string> = {
  drop_in: "Drop-in",
  booking: "Booked courts",
};

/** The player's name, or "Player" when the profile is not resolvable here. */
export function kioskPlayerName(
  item: Pick<KioskCheckinItem, "display_name">,
): string {
  return item.display_name?.trim() || "Player";
}

/** "#last4", the same handle as everywhere else; null without a player. */
export function kioskPlayerTag(
  item: Pick<KioskCheckinItem, "player_user_id">,
): string | null {
  return item.player_user_id ? playerHandleSuffix(item.player_user_id) : null;
}

export function kioskInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

function unitsLeft(units: number | null | undefined): string | null {
  if (units === null || units === undefined) return null;
  return units === 1 ? "1 visit left" : `${units} visits left`;
}

/**
 * The second line of a scan: what a green scan unlocked (and visits left on
 * the pass it used), or why a red scan was refused.
 */
export function kioskScanDetail(item: KioskCheckinItem): string {
  if (item.outcome === "red") {
    return item.reason ? KIOSK_REASON_LABEL[item.reason] : "Not accepted";
  }
  const unlocked =
    item.unlocked === "court_booking"
      ? "Court unlocked"
      : item.unlocked === "drop_in_queue"
        ? "Drop-in unlocked"
        : "Checked in";
  const units = unitsLeft(item.units_left);
  const detail = units ? `${unlocked} · ${units}` : unlocked;
  return item.undone_at ? `Undone · ${detail}` : detail;
}

/** The toast after an undo: the visit comes back only if the admission used one. */
export function kioskUndoMessage(
  name: string,
  result: KioskUndoResult,
): string {
  if (result.visit_restored === true) {
    return `${name} checked out. The visit went back to their pass.`;
  }
  if (result.visit_restored === false) {
    return `${name} checked out. No visit was given back.`;
  }
  return `${name} checked out. No visit was used, so none was given back.`;
}

export type KioskDeviceStatus = "online" | "offline" | "waiting" | "revoked";

export function kioskDeviceStatus(device: KioskDevice): KioskDeviceStatus {
  if (device.state === "revoked") return "revoked";
  if (device.online) return "online";
  return device.last_seen_at ? "offline" : "waiting";
}

export const KIOSK_DEVICE_STATUS_LABEL: Record<KioskDeviceStatus, string> = {
  online: "Online",
  offline: "Offline",
  waiting: "Not paired",
  revoked: "Revoked",
};

function formatInstant(
  value: string | null | undefined,
  formatter: Intl.DateTimeFormat,
) {
  if (!value) return null;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? null : formatter.format(instant);
}

/** "online · last seen 6:41 PM", "offline since 2:10 PM", "waiting to pair". */
export function kioskDeviceLine(
  device: KioskDevice,
  timeFormatter: Intl.DateTimeFormat,
  dateFormatter: Intl.DateTimeFormat,
): string {
  const purposes = device.purposes
    .map((p) => KIOSK_PURPOSE_LABEL[p])
    .join(", ");
  const status = kioskDeviceStatus(device);
  const seen = formatInstant(device.last_seen_at, timeFormatter);
  if (status === "revoked") {
    const revoked = formatInstant(device.revoked_at, dateFormatter);
    return revoked ? `revoked ${revoked}` : "revoked";
  }
  if (status === "online")
    return `${purposes} · online${seen ? ` · last seen ${seen}` : ""}`;
  if (status === "offline")
    return `${purposes} · offline since ${seen ?? "earlier"}`;
  return `${purposes} · waiting to pair`;
}

/** The toolbar's kiosk status: the first active device and whether it is online. */
export function kioskToolbarStatus(
  mode: KioskMode | null,
  devices: readonly KioskDevice[],
): { label: string; tone: "online" | "offline" | "off" } {
  if (mode === "off") return { label: "Kiosk off", tone: "off" };
  const active = devices.filter((d) => d.state === "active");
  if (active.length === 0) return { label: "No kiosk yet", tone: "off" };
  const online = active.filter((d) => d.online);
  const shown = online[0] ?? active[0];
  const more = active.length > 1 ? ` +${active.length - 1}` : "";
  return {
    label: `Kiosk · ${shown.name} · ${shown.online ? "online" : "offline"}${more}`,
    tone: online.length > 0 ? "online" : "offline",
  };
}
